import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ReportHeader, ReportRenderer } from '../components/reports';

const API_URL = '/api'; //'http://localhost:8002/api'

// 반응형 limit 계산
const getItemsPerPage = () => {
    const width = window.innerWidth;
    if (width < 768) return 5;  // 모바일
    if (width < 1024) return 10; // 태블릿
    return 20; // 데스크톱
};

export const MyReports = () => {
    const [reports, setReports] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [expandedReports, setExpandedReports] = useState(new Set());
    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [itemsPerPage, setItemsPerPage] = useState(getItemsPerPage());
    const { user } = useAuth();

    const observerTarget = useRef(null);

    // 화면 크기 변경 감지
    useEffect(() => {
        const handleResize = () => {
            setItemsPerPage(getItemsPerPage());
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (user?.user_id) {
            loadUserReports(user.user_id);
        }
    }, [user]);

    const loadUserReports = async (userId) => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${API_URL}/reports/user/${userId}`, {
                params: { limit: itemsPerPage }
            });
            setReports(response.data.reports);
            setHasMore(response.data.has_more);
            setNextCursor(response.data.next_cursor);
        } catch (error) {
            console.error('Error loading user reports:', error);
            setError('보고서를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const loadMoreReports = useCallback(async () => {
        if (!hasMore || loadingMore || !user?.user_id) return;

        try {
            setLoadingMore(true);
            setError(null);
            const response = await axios.get(`${API_URL}/reports/user/${user.user_id}`, {
                params: {
                    limit: itemsPerPage,
                    cursor: nextCursor
                }
            });
            setReports(prev => [...prev, ...response.data.reports]);
            setHasMore(response.data.has_more);
            setNextCursor(response.data.next_cursor);
        } catch (error) {
            console.error('Error loading more reports:', error);
            setError('추가 보고서를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoadingMore(false);
        }
    }, [hasMore, loadingMore, nextCursor, itemsPerPage, user?.user_id]);

    // Intersection Observer로 무한 스크롤 구현
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    loadMoreReports();
                }
            },
            { threshold: 0.1 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [hasMore, loadingMore, loadMoreReports]);

    const toggleReport = (reportId) => {
        setExpandedReports(prev => {
            const newSet = new Set(prev);
            if (newSet.has(reportId)) {
                newSet.delete(reportId);
            } else {
                newSet.add(reportId);
            }
            return newSet;
        });
    };

    const toggleCategory = (reportId, categoryIdx, event) => {
        event.stopPropagation();
        const key = `${reportId}-${categoryIdx}`;
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <p>보고서를 불러오는 중입니다...</p>
            </div>
        );
    }

    if (reports.length === 0) {
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <p>맞춤 보고서가 없습니다.</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold mb-6">내 리포트</h1>
            {reports.map(report => (
                <Card key={report.id} className="mb-6">
                    <div
                        className="cursor-pointer"
                        onClick={() => toggleReport(report.id)}
                    >
                        <ReportHeader
                            report={report}
                            isExpanded={expandedReports.has(report.id)}
                        />
                    </div>

                    {expandedReports.has(report.id) && (
                        <CardContent>
                            {/* 논문 정보가 있는 경우 상단에 표시 */}
                            {report.report_content.title && (
                                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                                    {report.report_content.arxiv_id && (
                                        <div className="mt-1">
                                            <a
                                                href={`https://arxiv.org/abs/${report.report_content.arxiv_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary-600 hover:text-primary-800 flex items-center gap-1"
                                            >
                                                arXiv: {report.report_content.arxiv_id}
                                                <ExternalLink className="w-4 h-4" />
                                            </a>
                                        </div>
                                    )}
                                    {report.authors && (
                                        <div className="mt-1 text-sm text-gray-600">
                                            저자: {report.authors.join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}

                            <ReportRenderer
                                report={report}
                                expandedCategories={expandedCategories}
                                onToggleCategory={toggleCategory}
                            />

                            {report.report_content.papers && Array.isArray(report.report_content.papers) && (
                                <div className="mt-4">
                                    <h3 className="font-medium mb-2">Related Papers:</h3>
                                    <ul className="space-y-2">
                                        {report.report_content.papers.map(paper => (
                                            <li key={paper.arxiv_id || paper.id} className="flex items-start gap-2">
                                                {paper.arxiv_id && (
                                                    <a
                                                        href={`https://arxiv.org/abs/${paper.arxiv_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary-600 hover:text-primary-800 flex items-center gap-1"
                                                    >
                                                        {paper.arxiv_id}
                                                        <ExternalLink className="w-4 h-4" />
                                                    </a>
                                                )}
                                                <span>- {paper.title}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </CardContent>
                    )}
                </Card>
            ))}

            {/* 무한 스크롤 트리거 */}
            {hasMore && !error && (
                <div ref={observerTarget} className="flex justify-center py-8">
                    {loadingMore && (
                        <div className="flex items-center gap-2 text-gray-600">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>로딩 중...</span>
                        </div>
                    )}
                </div>
            )}

            {/* 에러 발생 시 재시도 버튼 */}
            {error && (
                <div className="flex flex-col items-center gap-4 py-8">
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={loadMoreReports}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <RefreshCw className="w-5 h-5" />
                        <span>다시 시도</span>
                    </button>
                </div>
            )}

            {/* 모든 데이터 로드 완료 */}
            {!hasMore && reports.length > 0 && (
                <div className="text-center py-8 text-gray-500">
                    모든 보고서를 불러왔습니다.
                </div>
            )}
        </div>
    );
};

export default MyReports;
